import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';

import Container from '../components/Container';
import { getFirebaseAuth } from '../lib/firebase';
import { TESTFLIGHT_URL } from '../lib/platform';

type Status = 'idle' | 'verifying' | 'success' | 'error' | 'no-code';

/**
 * `/verify` — the target of Firebase Auth's "verify email" action URL.
 *
 * In production the URL looks like:
 *   https://djinncoach.com/verify?mode=verifyEmail&oobCode=ABC123&apiKey=...&lang=en
 *
 * On iOS / Android, Universal Links / App Links intercept the URL before the
 * browser ever opens — the user lands directly in the app. This page only
 * renders on desktop browsers (or when link interception failed), and serves
 * two purposes:
 *
 *   1. Actually verify the email by calling `applyActionCode` against the
 *      Firebase Auth REST API, so the user's session is verified regardless
 *      of which device they end up logging in on.
 *   2. Offer the TestFlight link so they can finish on their phone.
 */
export default function Verify() {
  const [params] = useSearchParams();
  const oobCode = params.get('oobCode');
  const mode = params.get('mode');

  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const request = useRef<{ code: string; promise: Promise<void> } | null>(null);

  useEffect(() => {
    if (!oobCode || mode !== 'verifyEmail') {
      setStatus('no-code');
      return;
    }
    const auth = getFirebaseAuth();
    if (!auth) {
      setStatus('error');
      setError(
        'This page is misconfigured — Firebase env vars are missing. Please contact support.',
      );
      return;
    }

    setStatus('verifying');
    setError(null);
    let active = true;
    if (request.current?.code !== oobCode) {
      request.current = { code: oobCode, promise: applyActionCode(auth, oobCode) };
    }
    request.current.promise
      .then(() => { if (active) setStatus('success'); })
      .catch((err: unknown) => {
        if (!active) return;
        setStatus('error');
        setError(
          err instanceof Error
            ? translateFirebaseError(err.message)
            : 'Verification failed. The link may have expired.',
        );
      });
    return () => { active = false; };
  }, [oobCode, mode]);

  return (
    <Container narrow>
      <div className="djinn-card p-8">
        <h1 className="text-2xl font-semibold tracking-tight text-djinn-text">
          Verify your email
        </h1>

        {status === 'verifying' && (
          <p className="mt-4 text-djinn-text-2">Verifying your account…</p>
        )}

        {status === 'success' && (
          <div className="mt-4 space-y-4 text-djinn-text-2">
            <p>
              ✅ Your email is verified. You can now sign in to Djinn Coach from your phone.
            </p>
            <GetTheAppButton />
          </div>
        )}

        {status === 'no-code' && (
          <p className="mt-4 text-djinn-text-2">
            This link is missing a verification code or has been opened incorrectly. Please
            re-open the link from your verification email, or request a new one from the
            Djinn Coach app.
          </p>
        )}

        {status === 'error' && (
          <div className="mt-4 space-y-4 text-djinn-text-2">
            <p className="text-djinn-danger">{error}</p>
            <p>
              Open the Djinn Coach app and tap <em>Resend verification email</em> to receive
              a new link.
            </p>
            <GetTheAppButton />
          </div>
        )}
      </div>

    </Container>
  );
}

/**
 * Single CTA shown on the Verify page while DJINN is on TestFlight.
 */
function GetTheAppButton() {
  return (
    <a href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer" className="djinn-btn-primary">Get the app on TestFlight</a>
  );
}

function translateFirebaseError(message: string): string {
  if (message.includes('auth/invalid-action-code')) {
    return 'This verification link has expired or has already been used.';
  }
  if (message.includes('auth/user-disabled')) {
    return 'Your account has been disabled. Please contact support.';
  }
  return 'Verification failed. The link may have expired.';
}
